import React, { useState } from 'react';
import { Download, X, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import { storage } from '../../utils/storage';

const DataManagement = () => {
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { user, logout } = useAuth();
  const { theme } = useTheme();

  const exportData = () => {
    const data = {
      user: user,
      moodData: storage.get('moodData') || [],
      pomodoroSessions: storage.get('pomodoroSessions') || [],
      exportDate: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `wellness-data-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const deleteAllData = () => {
    storage.remove('moodData');
    storage.remove('pomodoroSessions');
    setShowDeleteModal(false);
    logout();
  };

  return (
    <>
      <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
        <h3 className="font-semibold mb-4">Privacy & Data</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Data Storage</h4>
            <p className="text-sm text-gray-500 mb-3">
              All your data is stored locally on this device. No information is sent to external servers.
            </p>
            
            <div className="space-y-2">
              <button
                onClick={() => setShowExportModal(true)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <Download className="w-5 h-5" />
                  <span>Export My Data</span>
                </div>
                <ChevronRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
              >
                <div className="flex items-center space-x-3">
                  <X className="w-5 h-5" />
                  <span>Delete All Data</span>
                </div>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full`}>
            <h3 className="text-xl font-semibold mb-4">Export Your Data</h3>
            <p className="text-gray-600 mb-6">
              Download all your mood entries and focus session data as a JSON file. 
              You can use this to backup your data or transfer it to another device.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  exportData();
                  setShowExportModal(false);
                }}
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Export
              </button>
              <button
                onClick={() => setShowExportModal(false)}
                className="flex-1 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full`}>
            <h3 className="text-xl font-semibold mb-4 text-red-600">Delete All Data</h3>
            <p className="text-gray-600 mb-6">
              This will permanently delete all your mood entries, focus sessions, and account data. 
              This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={deleteAllData}
                className="flex-1 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Delete Everything
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DataManagement;